import type { Fact } from "../../common/fact-table";
import { COUNTRIES, type Country } from "./list";

// ISO 3166 assigns Kosovo no code at all: XK is from the range it leaves users to assign and UNK is the data's own,
// so a label naming ISO 3166-1 beside either would be claiming for it what ISO never did.
export function isoCodes(country: Country): Fact[] {
  const assigned = country.status === "officially-assigned";
  const label = (part: string) => assigned ? `ISO 3166-1 ${part.toLowerCase()}` : `${part}, not ISO-assigned`;
  return [
    { label: label("Alpha-2"), value: country.cca2 },
    { label: label("Alpha-3"), value: country.cca3 },
    { label: label("Numeric"), value: country.ccn3 },
  ];
}

// The root is the country's own prefix and the suffixes are what a number carries after it, which for most countries
// is one dialling code and for the North American plan is every area code in it.
export function callingCodes(country: Country): string[] {
  const { root, suffixes } = country.idd;
  if (!root) return [];
  return suffixes.length > 0 ? suffixes.map((suffix) => root + suffix) : [root];
}

// A land area of -1 is the data saying it does not know, which is not the same as a country of no size.
export function areaText(country: Country): string {
  if (country.area < 0) return "";
  const imperial = `(${number(country.area / KM2_PER_SQUARE_MILE)}${NBSP}sq${NBSP}mi)`;
  return `${number(country.area)}${NBSP}km² ${imperial}`;
}

// A quantity and its unit are one word for the purpose of wrapping: the column these sit in is half a card wide, and
// the break would otherwise land between the number and the unit, or leave "mi)" alone on a line of its own.
const NBSP = "\u00a0";

// The international definition of the mile, squared.
const KM2_PER_SQUARE_MILE = 2.589988110336;

export function decimalDegrees(country: Country): string {
  const [latitude, longitude] = country.latlng;
  return `${latitude}, ${longitude}`;
}

export function coordinates(country: Country): string {
  const [latitude, longitude] = country.latlng;
  return `${degrees(latitude, "N", "S")} ${degrees(longitude, "E", "W")}`;
}

// Rounded to whole seconds before it is split, so 69.96666666 carries into 69°58′ rather than reading 69°57′60″.
function degrees(value: number, positive: string, negative: string): string {
  const total = Math.round(Math.abs(value) * 3600);
  const minutes = Math.floor(total / 60) % 60;
  return `${Math.floor(total / 3600)}°${pad(minutes)}′${pad(total % 60)}″${value < 0 ? negative : positive}`;
}

export function currencyRows(country: Country): { code: string; name: string; symbol: string }[] {
  return Object.entries(country.currencies).map(([code, currency]) => ({ code, ...currency }));
}

export function languageRows(country: Country): { code: string; name: string }[] {
  return Object.entries(country.languages).map(([code, name]) => ({ code, name }));
}

export function nativeNameRows(country: Country): { language: string; common: string; official: string }[] {
  return Object.entries(country.name.native).map(([code, name]) => ({ language: languageName(code), ...name }));
}

// Masculine and feminine where a language inflects them, which is why they are two columns and not one.
export function demonymRows(country: Country): { language: string; masculine: string; feminine: string }[] {
  return Object.entries(country.demonyms).map(([code, forms]) => ({
    language: languageName(code),
    masculine: forms.m,
    feminine: forms.f,
  }));
}

// Intl knows the 23 languages the translations are written in and most of the 153 that are spoken; the ones it does
// not are named by the data itself, which is where every other language name on the page comes from anyway.
export function languageName(code: string): string {
  const named = LANGUAGE_DISPLAY.of(code);
  if (named && named !== code) return named;
  return DATA_LANGUAGE_NAMES.get(code) ?? code;
}

const LANGUAGE_DISPLAY = new Intl.DisplayNames(["en"], { type: "language", fallback: "code" });

const DATA_LANGUAGE_NAMES = new Map(
  COUNTRIES.flatMap((country) => Object.entries(country.languages)),
);

function number(value: number): string {
  return NUMBER_FORMAT.format(value);
}

// Grouped, because six digits of land area unseparated is a number nobody reads at a glance, and to two places
// because Monaco is 2.02 km² and a whole number would round it away.
const NUMBER_FORMAT = new Intl.NumberFormat("en", { maximumFractionDigits: 2 });

function pad(value: number): string {
  return String(value).padStart(2, "0");
}
